import { useRef } from "react";

import { AdminAreaHierarchy } from "@/types/adminArea";

import { ChevronDown, ChevronRight } from "lucide-react";

type TreeNodeProps = {
  node: AdminAreaHierarchy;
  depth: number;
  isExpanded: boolean;
  isSelected: boolean;
  isIndeterminate: boolean;
  onToggleExpand: (id: string) => void;
  onToggleSelect: (
    checked: boolean,
    node: AdminAreaHierarchy,
    allChildren: string[]
  ) => void;
};

const TREE_INDENT_PER_LEVEL = 20; // pixels

export const TreeNode = ({
  node,
  depth,
  isExpanded,
  isSelected,
  isIndeterminate,
  onToggleExpand,
  onToggleSelect
}: TreeNodeProps) => {
  const hasChildren = node.sub_areas && node.sub_areas.length > 0;

  /** collect all children recursively */
  const collectChildren = (n: AdminAreaHierarchy): string[] => [
    n.id,
    ...(n.sub_areas ? n.sub_areas.flatMap(collectChildren) : [])
  ];

  const allChildren = collectChildren(node);

  /** Set indeterminate state on checkbox for partially selected parent nodes */
  const checkboxRef = useRef<HTMLInputElement>(null);
  if (checkboxRef.current) checkboxRef.current.indeterminate = isIndeterminate;

  const chevronIcon = isExpanded ? (
    <ChevronDown className="w-4 h-4" />
  ) : (
    <ChevronRight className="w-4 h-4" />
  );

  return (
    <div
      className="flex items-center cursor-pointer"
      style={{ paddingLeft: depth * TREE_INDENT_PER_LEVEL }} // indent 20px per level
    >
      {/* Chevron */}
      <button
        className="w-6 h-6 flex items-center justify-center text-gray-600"
        onClick={() => hasChildren && onToggleExpand(node.id)}
      >
        {hasChildren ? chevronIcon : <div className="w-4 h-4" />}
      </button>

      {/* Checkbox */}
      <div className="w-6 h-6 flex items-center justify-center">
        <input
          ref={checkboxRef}
          id={node.id}
          aria-label={node.name}
          type="checkbox"
          className="mr-2 w-3 h-3 accent-primary-teal"
          checked={isSelected}
          onChange={(e) => onToggleSelect(e.target.checked, node, allChildren)}
        />
      </div>

      {/* Label */}
      <label htmlFor={node.id} className="text-txtBody text-xs">
        {node.name}
      </label>
    </div>
  );
};
